import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, ShoppingBasket } from "lucide-react";
import { api } from "../lib/api";
import { useShop, money, isPerishable } from "../lib/shop";
import { StockBadge } from "../components/StockBadge";

export default function ProductDetail() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [variation, setVariation] = useState(null);
    const [qty, setQty] = useState(1);
    const { addToBasket, settings } = useShop();

    useEffect(() => {
        api.get(`/products/${id}`)
            .then((r) => {
                setProduct(r.data);
                if ((r.data.variations || []).length) setVariation(r.data.variations[0].name);
            })
            .catch(() => setNotFound(true));
    }, [id]);

    if (notFound)
        return (
            <main className="min-h-[50vh] flex items-center justify-center" data-testid="product-not-found">
                <div className="text-center">
                    <p className="text-slate-500 mb-4">Sorry, we couldn't find that product.</p>
                    <Link to="/products" className="text-[#0033A0] font-bold hover:underline">Back to products</Link>
                </div>
            </main>
        );
    if (!product) return <main className="min-h-[50vh]" data-testid="product-loading" />;

    const variations = product.variations || [];
    const current = variations.length ? variations.find((v) => v.name === variation) : null;
    const price = current ? current.price : product.sale_price || product.price;
    const stock = current ? current.stock : product.stock;
    const byWeight = !!product.sold_by_weight && !variations.length;
    const qtyStep = byWeight ? 0.25 : 1;
    const minQty = byWeight ? 0.25 : 1;
    const maxQty = stock != null && stock > 0 ? stock : 99;
    const outOfStock = stock != null && stock <= 0;
    const canBuy = price != null && !outOfStock;
    const fmtQty = (q) => (byWeight ? `${parseFloat(Number(q).toFixed(2))} kg` : q);

    return (
        <main data-testid="product-detail-page" className="bg-slate-50 min-h-screen py-10 md:py-16">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <nav className="text-sm text-slate-500 mb-6" aria-label="Breadcrumb">
                    <Link to="/products" className="hover:text-[#0033A0]" data-testid="breadcrumb-products">Our Products</Link>
                    <span className="mx-2">/</span>
                    <span className="text-slate-900 font-bold">{product.name}</span>
                </nav>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden md:grid md:grid-cols-2">
                    <div className="aspect-square md:aspect-auto bg-slate-50">
                        <img src={product.image} alt={product.name} className="h-full w-full object-cover" data-testid="product-image" />
                    </div>
                    <div className="p-6 md:p-10 flex flex-col">
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                            <span className="inline-block rounded-full bg-[#40E0D0]/20 text-[#0033A0] text-xs font-bold px-3 py-1" data-testid="product-category">
                                {product.category}
                            </span>
                            <StockBadge product={current ? { ...product, stock } : product} />
                        </div>
                        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900" data-testid="product-name">
                            {product.name}
                        </h1>
                        <p className="mt-3 text-slate-600 leading-relaxed" data-testid="product-description">{product.description}</p>

                        {variations.length > 0 && (
                            <div className="mt-6">
                                <p className="text-sm font-bold text-slate-900 mb-2">Options</p>
                                <div className="flex flex-wrap gap-2" data-testid="variation-options" role="radiogroup" aria-label="Product options">
                                    {variations.map((v) => (
                                        <button
                                            key={v.name}
                                            type="button"
                                            role="radio"
                                            aria-checked={variation === v.name}
                                            data-testid={`variation-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            onClick={() => setVariation(v.name)}
                                            className={`rounded-full px-4 py-2 text-sm font-heading font-bold transition-colors ${
                                                variation === v.name
                                                    ? "bg-[#0033A0] text-white"
                                                    : "bg-white border-2 border-slate-200 text-slate-700 hover:border-[#0033A0]"
                                            }`}
                                        >
                                            {v.name}
                                            {v.price != null && <span className="ml-1 font-normal">· {money(v.price)}</span>}
                                            {v.stock === 0 && <span className="ml-1 text-red-500">(out)</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
                            {price != null ? (
                                <p className="font-heading text-3xl font-extrabold text-[#0033A0]" data-testid="product-price">
                                    {money(price)}
                                    <span className="block text-sm font-normal text-slate-400">
                                        {(current?.unit || product.unit) !== "each" ? current?.unit || product.unit : "each"}
                                    </span>
                                </p>
                            ) : (
                                <p className="text-slate-500 font-bold" data-testid="product-price-unset">
                                    Available in store — call us for today's price
                                </p>
                            )}
                            {canBuy && (
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="flex items-center rounded-full border-2 border-slate-200">
                                        <button type="button" aria-label={byWeight ? "Decrease weight" : "Decrease quantity"} data-testid="qty-minus"
                                            onClick={() => setQty(Math.max(minQty, Math.round((qty - qtyStep) * 100) / 100))}
                                            className="p-2.5 text-[#0033A0] hover:bg-slate-50 rounded-l-full">
                                            <Minus className="h-4 w-4" />
                                        </button>
                                        <span className={`${byWeight ? "w-16" : "w-10"} text-center font-heading font-bold`} data-testid="qty-value">{fmtQty(qty)}</span>
                                        <button type="button" aria-label={byWeight ? "Increase weight" : "Increase quantity"} data-testid="qty-plus"
                                            onClick={() => setQty(Math.min(maxQty, Math.round((qty + qtyStep) * 100) / 100))}
                                            className="p-2.5 text-[#0033A0] hover:bg-slate-50 rounded-r-full">
                                            <Plus className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid="add-to-basket-button"
                                        onClick={() => addToBasket(product, current, qty)}
                                        className="inline-flex items-center gap-2 rounded-full bg-[#0033A0] text-white px-6 py-3 font-heading font-bold hover:bg-[#002277] transition-colors"
                                    >
                                        <ShoppingBasket className="h-5 w-5" /> Add to Basket{byWeight && price != null ? ` · ${money(price * qty)}` : ""}
                                    </button>
                                </div>
                            )}
                        </div>
                        {outOfStock && (
                            <p className="mt-4 text-red-600 font-bold" data-testid="out-of-stock-note">
                                Out of stock — check back soon or call us on 0118 229 5059.
                            </p>
                        )}
                        <div className="mt-6 flex flex-wrap gap-2" data-testid="fulfilment-badges">
                            {[
                                { on: product.allow_collection !== false, label: "Click & Collect" },
                                { on: product.allow_local_delivery !== false, label: "Local Delivery" },
                                { on: product.allow_courier_delivery !== false, label: "Courier Delivery" },
                            ].map((f) => (
                                <span key={f.label}
                                    className={`rounded-full px-3 py-1 text-xs font-bold ${f.on ? "bg-[#40E0D0]/20 text-[#0033A0]" : "bg-slate-100 text-slate-400 line-through"}`}
                                    data-testid={`fulfilment-badge-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                    {f.label}
                                </span>
                            ))}
                        </div>
                        {isPerishable(product, settings) && (
                            <p className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="perishable-badge">
                                Fresh & perishable — collection orders are held for {settings.perishable_hold_hours} hours after your chosen collection time.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
